import { ResponseFormat } from "../enums";
import { DecimationFactors } from "../utilities/scenarioUtilities";
import { TrackingDataParserInput } from "./TrackingTransformer";


// POSITIONAL DATA TYPES

export type StandardPlayerFrame = {
    id: string;
    x: number;
    y: number;
}

export type StandardBallFrame = {
    x: number;
    y: number;
    z?: number;
    possessor_id?: number;
}

export type StandardMatchFrame = {
    id: number;
    timestamp: number;
    period: number;
    players: StandardPlayerFrame[];
    ball: StandardBallFrame;
}

// METADATA TYPES

export type StandardPlayerData = {
    id: string;
    short_name: string;
    first_name: string;
    last_name: string;
    team_id: string;
    position: string;
    position_group: string;
    jersey_number: number;
}

export type StandardTeamData = {
    id: string;
    name: string;
    short_name: string;
    color1?: string;
    color2?: string;
}

export type StandardMatchData = {
    match_id: string;
    home_team: StandardTeamData;
    away_team: StandardTeamData;
    players: StandardPlayerData[];
    start_timestamp: number;
    end_timestamp: number;
    pitch_length: number;
    pitch_width: number;
}

// EVENT TYPES

export type StandardEventTag = {
    group_id: number;
    name: string;
}

export type StandardEventTagGroup = {
    id: number;
    name: string;
    description: string;
}

export type StandardEventCategory = {
    id: number;
    name: string;
    color: string;
}

export type StandardEvent = {
    id: number;
    start_time: number;
    end_time: number;
    category_id: number;
    tags: StandardEventTag[];
    type?: string;
    x?: number;
    y?: number;
    player_id: string;
    team_id?: string;
    target_player_id?: string;
    outcome?: string;
    pass_height?: number;
    pass_distance?: number;
}

export type StandardMatchEvents = {
    tag_groups: StandardEventTagGroup[];
    categories: StandardEventCategory[];
    events: StandardEvent[];
}

// IMPORT TYPES

export type TrackingDataStandardiser = (
    inputs: TrackingDataParserInput[],
    homeTeamId: string
) => {
    frames: StandardMatchFrame[];
    metadata: StandardMatchData;
    events: StandardMatchEvents;
};

export enum ImportDataType {
    Position = 'position',
    Events = 'events',
    Metadata = 'metadata',
    Errors = 'errors'
}

export enum MatchStatus {
    Pending = 'PENDING',
    Updating = 'UPDATING',
    Complete = 'COMPLETE',
    Error = 'ERROR',
    Unavailable = 'UNAVAILABLE'
}

export type ImportStreamContext = {
    dataType: ImportDataType;
    apiUrl: string;
    fileName: string;
    extension: string;
    contentType: string;
    responseFormat: ResponseFormat;
    proxyDev?: boolean;
    resolveApiData: (response: any) => Promise<any>;
    getUploadData: (stream: ImportStreamContext, data: any) => Promise<any[]>;
    resolveCacheData: (stream: ImportStreamContext, request: any, bucket: any, cacheRecord: any, tempFiles: Array<string>) => Promise<any>;
    tempFilePath?: string;
    data?: any;
    storeCount?: number;
    storeLength?: number;
    startTime?: number;
}

export type GeneratorSettings = {
    decimationThresholds?: DecimationFactors;
    segmentMergeDistance?: number;
    recalculateGroundPasses?: boolean;
    doEnsureBallProximity?: boolean;
    mergeSegments?: boolean;
    minProximity?: number;
};

export const DefaultGeneratorSettings: GeneratorSettings = {
    decimationThresholds: { distance: 1.5, angle: 20, oscillationAngle: 10, speed: 5, acceleration: 1 },
    segmentMergeDistance: 3,
    recalculateGroundPasses: true,
    doEnsureBallProximity: true,
    mergeSegments: true,
    minProximity: 0.25,
};

export type TrackingDataTransformer = {
    streams: ImportStreamContext[];
    postProcess?: (req: any, streams: ImportStreamContext[], bucket: any) => Promise<void>;
    transform: (req: any, streams: ImportStreamContext[]) => Promise<{
        frames: StandardMatchFrame[];
        metadata: StandardMatchData;
        events: StandardMatchEvents;
    }>;
    generatorSettings?: GeneratorSettings;
}